import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatSelectionList } from '@angular/material/list';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, startWith, switchMap } from 'rxjs/operators';
import { DIR, Sort } from '../../utils/sort';
import { IFilterableItem } from './filterable-item';

@Component({
    selector: 'app-filterable-list',
    templateUrl: './filterable-list.component.html',
    styleUrls: ['./filterable-list.component.scss'],
    standalone: false
})
export class FilterableListComponent implements OnDestroy {
  @Input()
  title = 'Items';

  @Input()
  tooltip = '';

  @Input()
  placeholder = '';

  _items: IFilterableItem[] = [];
  @Input()
  set items(items: IFilterableItem[]) {
    this.clearSelection();
    this._items = items.sort(this.sortFilterableItem);
    this.filteredItemsCount = items.length;
    this.inputChangeSubject.next('');
    this.searchText = '';
  }

  @Input()
  isExpandable = false;

  @Output()
  itemInsertedEvent = new EventEmitter<IFilterableItem>(); // TODO: stupid event name

  @ViewChild('matSelectionList')
  matSelectionList!: MatSelectionList;

  items$: Observable<IFilterableItem[]>;
  filteredItemsCount = 0;
  searchText = ''; // this is just used to reset the input value when the items change
  selectedItem: IFilterableItem | undefined;

  expanded = true;

  private inputChangeSubject = new Subject<string>();
  private inputChange$ = this.inputChangeSubject.asObservable();

  private itemInsertEventSubject = new Subject<IFilterableItem>();
  private itemInsertEventSubscription: Subscription;

  constructor() {
    this.items$ = this.inputChange$.pipe(
      debounceTime(250),
      startWith(''), // force all items to be shown initially
      switchMap((value) => this.filter(value))
    );

    this.itemInsertEventSubscription = this.itemInsertEventSubject
      .pipe(debounceTime(250))
      .subscribe((item) => this.itemInsertedEvent.emit(item));
  }

  public clearSelection(): void {
    this.matSelectionList?.deselectAll();
    // this.matSelectionList?.selectedOptions.clear();
  }

  ngOnDestroy() {
    this.itemInsertEventSubscription?.unsubscribe();
  }

  onSearchInput(e: any): void {
    this.inputChangeSubject.next(e.target.value);
  }

  onSearchKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.clearSearch();
    }

    if (this.filteredItemsCount === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      this.matSelectionList.focus();
      e.preventDefault();
    }
  }

  onItemClick(item: IFilterableItem): void {
    this.selectedItem = item; // this in stops the selection change firing, remove and onSelectionChange is hit
    this.fireItemInserted(item);
  }

  clearSearch(): void {
    this.searchText = '';
    this.inputChangeSubject.next('');
  }

  toggleExpanded(): void {
    if (this.isExpandable) {
      this.expanded = !this.expanded;
    }
  }

  private filter(value: string): Observable<IFilterableItem[]> {
    value = value.toLowerCase();
    const result = this._items.filter((i) => i.label.toLowerCase().includes(value));
    this.filteredItemsCount = result.length;
    return of(result);
  }

  private fireItemInserted(item: IFilterableItem): void {
    this.itemInsertEventSubject.next(item);
  }

  private sortFilterableItem(a: IFilterableItem, b: IFilterableItem): number {
    return Sort.sortString(a.label, b.label, DIR.ASC);
  }
}
